import { useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
  editDpp,
  fetchAttachmentThumbnail,
  listTicketAttachments,
  type EditDppFields,
  type TicketAttachment,
} from "@/lib/dppMutations";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dropzone } from "@/components/ui/dropzone";
import { Spinner } from "@/components/ui/spinner";
import {
  RemoteThumbnail,
  SortableTile,
  Tile,
  coverStyle,
  gridStyle,
} from "@/components/dpp/imageTiles";

interface Media {
  url: string;
  mediaType?: string;
}

export interface EditDppSaved {
  content: Record<string, unknown>;
  reference: string;
  department: string;
}

interface EditDppFormProps {
  brand: string;
  model: string;
  serial: string;
  content: { name?: string; description?: string; medias?: Media[] };
  reference?: string;
  department?: string;
  billNumber?: string | null;
  onSuccess: (saved: EditDppSaved) => void;
}

type PassportImage =
  | { kind: "existing"; key: string; url: string }
  | { kind: "ticket"; key: string; attachment: TicketAttachment }
  | { kind: "upload"; key: string; file: File; previewUrl: string };

const DEPARTMENT_LABELS: Record<string, string> = {
  phigora: "Phigora",
  watchcentral: "Watch Central",
};
const DEPARTMENTS = ["phigora", "watchcentral"];

function ticketKey(a: TicketAttachment): string {
  return `ticket:${a.ticketId}:${a.attachmentId}`;
}

export function EditDppForm({
  brand,
  model,
  serial,
  content,
  reference: initialReference = "",
  department: initialDepartment = "",
  billNumber,
  onSuccess,
}: EditDppFormProps) {
  const [name, setName] = useState(content.name ?? "");
  const [description, setDescription] = useState(content.description ?? "");
  const [department, setDepartment] = useState(initialDepartment);
  const [reference, setReference] = useState(initialReference);
  const [images, setImages] = useState<PassportImage[]>(() =>
    (content.medias ?? [])
      .filter((m) => m.mediaType === "picture" || !m.mediaType)
      .map((m) => ({ kind: "existing" as const, key: m.url, url: m.url }))
  );
  const [removeUrls, setRemoveUrls] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<TicketAttachment[] | null>(
    null
  );
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    if (!billNumber) return;
    let cancelled = false;
    setAttachmentsLoading(true);

    listTicketAttachments(brand, model, serial)
      .then((listing) => {
        if (!cancelled) setAttachments(listing.attachments);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setAttachmentsError(
            apiErrorMessage(err, "Failed to load ticket photos")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAttachmentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brand, model, serial, billNumber]);

  useEffect(() => {
    return () => {
      for (const image of images) {
        if (image.kind === "upload") URL.revokeObjectURL(image.previewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleRemove(url: string) {
    setRemoveUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  }

  function addFromTicket(attachment: TicketAttachment) {
    setImages((prev) => [
      ...prev,
      { kind: "ticket", key: ticketKey(attachment), attachment },
    ]);
  }

  function addUploads(files: File[]) {
    if (files.length === 0) return;
    setImages((prev) => [
      ...prev,
      ...files.map((file, i) => ({
        kind: "upload" as const,
        key: `local:${Date.now()}:${i}:${file.name}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  }

  function dropImage(key: string) {
    setImages((prev) => {
      const target = prev.find((i) => i.key === key);
      if (target?.kind === "upload") URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.key !== key);
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setImages((prev) => {
      const from = prev.findIndex((i) => i.key === active.id);
      const to = prev.findIndex((i) => i.key === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to);
    });
  }

  const addedTicketKeys = new Set(
    images.filter((i) => i.kind === "ticket").map((i) => i.key)
  );
  const kept = images.filter(
    (i) => !(i.kind === "existing" && removeUrls.includes(i.url))
  );
  const frontKey = kept.length > 0 ? kept[0].key : null;

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const uploads = images.filter(
      (i): i is Extract<PassportImage, { kind: "upload" }> =>
        i.kind === "upload"
    );
    const uploadIndex = new Map(uploads.map((u, i) => [u.key, i]));

    const fields: EditDppFields = { content: { name, description } };
    if (removeUrls.length > 0) fields.removeMediaUrls = removeUrls;

    const ticketSelections = images.filter(
      (i): i is Extract<PassportImage, { kind: "ticket" }> => i.kind === "ticket"
    );
    if (ticketSelections.length > 0) {
      fields.ticketAttachments = ticketSelections.map((i) => ({
        ticketId: i.attachment.ticketId,
        attachmentId: i.attachment.attachmentId,
      }));
    }

    fields.mediaOrder = kept.map((image) =>
      image.kind === "existing"
        ? image.url
        : image.kind === "ticket"
          ? image.key
          : `upload:${uploadIndex.get(image.key) ?? 0}`
    );

    if (department && department !== initialDepartment) {
      fields.department = department;
    }
    if (reference.trim() !== initialReference.trim()) {
      fields.reference = reference.trim();
    }

    try {
      const result = await editDpp(
        brand,
        model,
        serial,
        fields,
        uploads.map((u) => u.file)
      );
      setSubmitted(true);
      onSuccess({
        content: result.content,
        reference: reference.trim(),
        department,
      });
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to update passport"));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
        Update saved. Changes are shown on the passport now.
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}
      >
        <div className="field">
          <Label>Brand</Label>
          <Input value={brand} disabled />
        </div>
        <div className="field">
          <Label>Model</Label>
          <Input value={model} disabled />
        </div>
        <div className="field">
          <Label>Serial</Label>
          <Input value={serial} disabled />
        </div>
      </div>

      <div className="field">
        <Label htmlFor="edit-name">Name</Label>
        <Input
          id="edit-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="field">
        <Label htmlFor="edit-desc">Description</Label>
        <Input
          id="edit-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="field">
        <Label htmlFor="edit-ref">Reference</Label>
        <Input
          id="edit-ref"
          placeholder="e.g. 126610LN"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="field">
        <Label htmlFor="edit-dept">Department</Label>
        <select
          id="edit-dept"
          className="select"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          disabled={submitting}
          style={{ padding: "10px 14px", fontSize: 13, appearance: "none" }}
        >
          {!initialDepartment && <option value="">Unassigned</option>}
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>
              {DEPARTMENT_LABELS[d] ?? d}
            </option>
          ))}
        </select>
      </div>

      {images.length > 0 && (
        <div className="field">
          <Label>Passport images — drag to reorder, first is the front</Label>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((i) => i.key)}
              strategy={rectSortingStrategy}
            >
              <div style={gridStyle}>
                {images.map((image) => {
                  const marked =
                    image.kind === "existing" && removeUrls.includes(image.url);
                  const isFront = image.key === frontKey;
                  return (
                    <SortableTile
                      key={image.key}
                      id={image.key}
                      dimmed={marked}
                      hoverIcon={marked ? "check" : "x"}
                      badge={isFront ? "Front" : undefined}
                      title={
                        image.kind === "existing"
                          ? marked
                            ? "Click to keep"
                            : "Click to remove"
                          : "Click to discard"
                      }
                      onClick={() =>
                        image.kind === "existing"
                          ? toggleRemove(image.url)
                          : dropImage(image.key)
                      }
                    >
                      {image.kind === "existing" && (
                        <img src={image.url} alt="" style={coverStyle} />
                      )}
                      {image.kind === "upload" && (
                        <img
                          src={image.previewUrl}
                          alt={image.file.name}
                          style={coverStyle}
                        />
                      )}
                      {image.kind === "ticket" && (
                        <RemoteThumbnail
                          alt={image.attachment.name}
                          load={() =>
                            fetchAttachmentThumbnail(
                              brand,
                              model,
                              serial,
                              image.attachment.ticketId,
                              image.attachment.attachmentId
                            )
                          }
                        />
                      )}
                    </SortableTile>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {billNumber && (
        <div className="field">
          <Label>Ticket photos for bill {billNumber} — click to add</Label>
          {attachmentsLoading && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                fontSize: 13,
                color: "var(--ink-soft)",
              }}
            >
              <Spinner /> Loading ticket photos…
            </div>
          )}
          {!attachmentsLoading &&
            attachments !== null &&
            attachments.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                No image or PDF attachments found on tickets for this bill.
              </div>
            )}
          {attachments !== null && attachments.length > 0 && (
            <div style={gridStyle}>
              {attachments.map((a) => {
                const key = ticketKey(a);
                const isAdded = addedTicketKeys.has(key);
                return (
                  <Tile
                    key={key}
                    dimmed={isAdded}
                    inert={isAdded}
                    hoverIcon={isAdded ? "check" : "plus"}
                    title={
                      isAdded
                        ? `${a.name} — already added`
                        : `${a.name} — click to add`
                    }
                    onClick={() => addFromTicket(a)}
                  >
                    <RemoteThumbnail
                      alt={a.name}
                      load={() =>
                        fetchAttachmentThumbnail(
                          brand,
                          model,
                          serial,
                          a.ticketId,
                          a.attachmentId
                        )
                      }
                    />
                  </Tile>
                );
              })}
            </div>
          )}
          {attachmentsError && (
            <div style={{ fontSize: 12, color: "var(--danger)" }}>
              {attachmentsError}
            </div>
          )}
        </div>
      )}

      <div className="field">
        <Label>Upload images from this device</Label>
        <Dropzone
          accept="image/*"
          variant="image"
          disabled={submitting}
          files={[]}
          onFiles={addUploads}
          hint="PNG, JPG or HEIC"
        />
      </div>

      {error && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : "Save changes"}
      </Button>
    </form>
  );
}
